/** The routes of the core airports reference (SA-1031 spec §5.3; the house list and save, SA-1054 §5.2). */
import { ApiError, apiClient } from './apiClient'
import type { Airport } from '@/types/airports'

type Wire = Record<string, unknown>

/** A row the server sent as null (or a list hole) is dropped, never handed to `toAirport` (SA-1056). */
const present = (w: Wire | null | undefined): w is Wire => w != null && typeof w === 'object'

export function toAirport(w: Wire): Airport {
  return {
    ident: w.ident as string, icao: (w.icao as string) ?? null, iata: (w.iata as string) ?? null, name: w.name as string,
    city: (w.city as string) ?? null, country: w.country as string, countryName: (w.country_name as string) ?? '',
    lat: w.lat as number, lon: w.lon as number, kind: w.kind as string, scheduledService: Boolean(w.scheduled_service),
    timezone: (w.timezone as string) ?? null, houseName: (w.house_name as string) ?? null, houseCity: (w.house_city as string) ?? null,
    retired: Boolean(w.retired), displayName: w.display_name as string, placeWords: (w.place_words as string) ?? '',
  }
}

export async function searchAirports(q: string, { limit = 5, iataOnly = false }: { limit?: number; iataOnly?: boolean } = {}): Promise<Airport[]> {
  const params = new URLSearchParams({ q, limit: String(limit) })
  if (iataOnly) params.set('iata_only', '1')
  const rows = await apiClient.get<Array<Wire | null>>(`/api/airports/search?${params.toString()}`)
  return (rows ?? []).filter(present).map(toAirport)
}

export async function resolveAirports(codes: string[]): Promise<Record<string, Airport>> {
  const body = await apiClient.post<{ rows?: Record<string, Wire | null> }>('/api/airports/resolve', { codes })
  return Object.fromEntries(
    Object.entries(body?.rows ?? {}).flatMap(([code, w]) => (present(w) ? [[code, toAirport(w)]] : [])),
  )
}

/** The operator's words for a station. Both keys are sent every time; an empty string clears the column. */
export interface AirportHousePatch {
  houseName: string
  houseCity: string
}

/** Every station carrying house words, retired ones included, by code (admin). */
export async function listHouseAirports(): Promise<Airport[]> {
  const rows = await apiClient.get<Array<Wire | null>>('/api/airports/house')
  return (rows ?? []).filter(present).map(toAirport)
}

/** `apiClient` has no PATCH helper; this is its `put` with the method changed. */
export async function saveAirportHouse(ident: string, patch: AirportHousePatch): Promise<Airport> {
  const path = `/api/airports/${encodeURIComponent(ident)}`
  const response = await apiClient.fetch(path, {
    method: 'PATCH',
    body: JSON.stringify({ house_name: patch.houseName, house_city: patch.houseCity }),
  })
  if (!response.ok) {
    let detail: unknown = null
    try {
      const body = await response.json()
      detail = body && typeof body === 'object' && 'detail' in body ? (body as { detail: unknown }).detail : body
    } catch {
      // no JSON body: the status alone words the refusal
    }
    throw new ApiError(`PATCH ${path} failed: ${response.statusText}`, response.status, detail)
  }
  return toAirport(await response.json())
}
